import React from 'react';
import { Building2, Users, Award, TrendingUp, Eye, Target, Heart, Lightbulb, HandshakeIcon, BookOpen, Mail, Phone, MapPin, Globe } from 'lucide-react';

const FirmProfileSection = () => {

  const stats = [
    { label: "Established", value: "2011", icon: Building2 },
    { label: "Partners", value: "5", icon: Users },
    { label: "Professionals", value: "40+", icon: Award },
    { label: "Years Excellence", value: "15+", icon: TrendingUp }
  ];

  const coreValues = [
    { number: "01", title: "Excellence", description: "Unwavering commitment to the highest standards of professional quality", icon: Award },
    { number: "02", title: "Integrity", description: "Conducting all affairs with transparency, honesty, and ethical rigor", icon: Heart },
    { number: "03", title: "Client Focus", description: "Placing client success at the center of everything we do", icon: Target },
    { number: "04", title: "Innovation", description: "Embracing progressive approaches to solve complex challenges", icon: Lightbulb },
    { number: "05", title: "Collaboration", description: "Building partnerships that create lasting value", icon: HandshakeIcon },
    { number: "06", title: "Continuous Learning", description: "Investing in knowledge to stay ahead of evolving landscapes", icon: BookOpen }
  ];

  const consultingPillars = [
    {
      id: 'ai-insights',
      title: "Latest AI Insights",
      subtitle: "GenAI · Agentic AI · Responsible AI",
      items: [
        "AI Trends & Quarterly Briefings",
        "GenAI Readiness Diagnostic",
        "Responsible AI Framework (DPDPA, RBI, India AI Act)",
        "Use-Case Discovery Labs",
        "Agentic AI Architecture Reviews",
        "AI for Finance & Tax (Copilots)"
      ]
    },
    {
      id: 'strategy-transactions',
      title: "Strategy & Transactions",
      subtitle: "M&A · Capital · Restructuring",
      items: [
        "Mergers & Acquisitions Advisory",
        "Capital Strategy & Fundraising",
        "Valuations & Financial Modeling",
        "Financial, Tax & Commercial Due Diligence",
        "Restructuring, Demergers & Slump Sales",
        "Post-Merger Integration"
      ]
    },
    {
      id: 'risk-consulting',
      title: "Risk Consulting Solutions",
      subtitle: "ERM · ICFR · Forensic",
      items: [
        "Enterprise Risk Management (COSO, ISO 31000)",
        "Internal Audit Transformation",
        "Internal Financial Controls (IFC / ICFR)",
        "Regulatory & Compliance Risk (SEBI, RBI, FEMA, DPDPA)",
        "Third-Party & Vendor Risk",
        "Forensic & Fraud Risk Investigations"
      ]
    },
    {
      id: 'technology-consulting',
      title: "Technology Consulting Solutions",
      subtitle: "Digital · Data · Cyber",
      items: [
        "Digital & Finance Transformation",
        "ERP & Platform Advisory (SAP, Oracle, NetSuite, Zoho, Tally)",
        "Data, Analytics & BI",
        "Cybersecurity & Cloud Risk (SOC 2, ISO 27001)",
        "RPA & Intelligent Automation",
        "Emerging Tech (Blockchain, Tokenization)"
      ]
    },
    {
      id: 'managed-services',
      title: "Managed Services",
      subtitle: "Operate · Outsource · Scale",
      items: [
        "Finance & Accounting Managed Services",
        "Tax Managed Services (GST, TDS, IT, TP)",
        "Payroll & HR Compliance",
        "Virtual CFO Services",
        "Regulatory & Secretarial Compliance",
        "Risk & Internal Audit as a Service"
      ]
    }
  ];

  const services = [
    {
      id: 1,
      title: "Assurance & Audit",
      items: [
        "Statutory Audit (Companies Act)",
        "Tax Audit (Income Tax Act)",
        "Internal, Concurrent & Bank Audit",
        "IPO Readiness Audit & Diligence",
        "ESG Audit & Sustainability Assurance",
        "Management, Systems & IS Audit"
      ]
    },
    {
      id: 2,
      title: "Taxation Services",
      items: [
        "Direct Tax Advisory & Compliance",
        "GST / Indirect Tax Consultancy",
        "Tax Representation & Litigation",
        "TDS & Professional Tax Compliance",
        "Transfer Pricing Documentation",
        "International Taxation & DTAA"
      ]
    },
    {
      id: 3,
      title: "Advisory & Consultancy",
      items: [
        "IPO Readiness & Listing Advisory",
        "Business Advisory & Virtual CFO",
        "Due Diligence & Investigations",
        "IFRS / Ind-AS Convergence",
        "Internal Financial Controls (ICFR)",
        "Working Capital & FP&A"
      ]
    },
    {
      id: 4,
      title: "Corporate, Foreign Subsidiary & Regulatory",
      items: [
        "Foreign Subsidiary Formation & Ongoing Compliance",
        "Technical Collaboration Coordination & FEMA Filings",
        "Foreign Branch, Liaison & Project Office Setup",
        "Company Incorporation (Pvt Ltd, LLP, OPC)",
        "Secretarial & ROC Compliance · FDI Reporting",
        "Startup India · ESOP · Fundraising"
      ]
    },
    {
      id: 5,
      title: "NRI & Cross-Border Services",
      items: [
        "Net Worth Certificates for Visa purposes",
        "Financial Statements for Visa applications",
        "DTAA & Foreign Tax Credit Consulting",
        "Property Transaction & Capital-Gains Advisory",
        "Repatriation Consulting & FEMA Compliance",
        "NRI Income Tax Returns · TDS · Form 15CA/CB"
      ]
    }
  ];

  const leadership = [
    {
      name: "CA. Bharath Rao K S",
      role: "Founder Partner",
      qualified: "2011",
      photo: "/leadership/bharath.png",
      expertise: ["Assurance & Audit", "Income Tax", "Management Consultancy", "Bank Concurrent Audit"]
    },
    {
      name: "CA. Virupakshappa M. Tuppad",
      role: "Partner",
      qualified: "2006",
      photo: "/leadership/tuppad.png",
      expertise: ["Company Law", "Bank Audit", "Virtual CFO", "Treasury Management"]
    },
    {
      name: "CA. Pradeep Kumar S.",
      role: "Partner",
      qualified: "2010",
      photo: "/leadership/pradeep.png",
      expertise: ["Indirect Taxation", "ROC Matters", "Statutory Audits", "MNC Consultancy"]
    },
    {
      name: "CA. Ramesh K S",
      role: "Senior Advisor",
      qualified: "1985",
      photo: "/leadership/ramesh.png",
      expertise: ["Public Sector Experience", "Finance & Accounts", "Systems Audit", "Accounting Policy Development"]
    }
  ];

  const industries = [
    { name: "Manufacturing", details: "Software, Electronics, Automobiles, Chemicals" },
    { name: "Technology", details: "IT Services, Software Products" },
    { name: "Financial Services", details: "Banking, Wealth Management, NBFCs" },
    { name: "Healthcare", details: "Hospitals, Clinics, Pharmaceuticals" },
    { name: "Education", details: "Institutions, EdTech" },
    { name: "Real Estate", details: "Development, Construction" },
    { name: "Hospitality", details: "Hotels, Restaurants" },
    { name: "Non-Profit", details: "Trusts, NGOs, Foundations" }
  ];

  return (
    <section id="firm-profile" className="relative bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&q=90"
            alt="Office"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#0099FF]/20 border border-[#0099FF]/50 px-4 py-2 rounded-none mb-6">
              <span className="text-[#0099FF] text-sm font-medium">FIRM PROFILE</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-4">R S T & Co.</h1>
            <p className="text-2xl text-gray-300 mb-3">Chartered Accountants</p>
            <p
              className="text-xl md:text-2xl text-[#0099FF] font-medium mb-8 tracking-wide"
              lang="kn"
              data-testid="profile-kannada-name"
            >
              ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ · ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು
            </p>
            <div className="flex items-center justify-center gap-8 text-gray-400 text-sm">
              <span>Established 2011</span>
              <span>•</span>
              <span>Bengaluru, India</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => {
              const IconComponent = stat.icon;
              return (
                <div key={idx} className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 text-center">
                  <IconComponent className="text-[#0099FF] mx-auto mb-3" size={32} />
                  <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-gray-300 text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <h2 className="text-4xl font-semibold text-gray-900 mb-8">Executive Summary</h2>
          <div className="prose max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              R S T & Co., Chartered Accountants, is a distinguished professional services firm established in 2011, offering comprehensive solutions across Assurance, Taxation, and Advisory services. Our multidisciplinary approach combines deep domain expertise with innovative thinking to deliver exceptional value to our clients.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We serve a diverse portfolio spanning manufacturing, technology, financial services, healthcare, and non-profit sectors. Our commitment to excellence, ethical practice, and client success has positioned us as a trusted partner for businesses seeking to navigate complex regulatory landscapes and achieve sustainable growth.
            </p>
          </div>
        </div>
      </div>

      {/* Vision & Mission */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <h2 className="text-4xl font-semibold text-gray-900 mb-16 text-center">Our Foundation</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <div className="bg-white p-10 border-l-4 border-[#0099FF] shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <Eye className="text-[#0099FF]" size={40} />
                <h3 className="text-2xl font-semibold text-gray-900">Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To be the premier Chartered Accountancy firm recognized for delivering transformative solutions that empower businesses to achieve excellence and sustainable growth.
              </p>
            </div>

            <div className="bg-white p-10 border-l-4 border-[#0099FF] shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <Target className="text-[#0099FF]" size={40} />
                <h3 className="text-2xl font-semibold text-gray-900">Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To provide insightful, ethical, and tailored professional services that exceed client expectations, foster long-term partnerships, and contribute meaningfully to the business ecosystem.
              </p>
            </div>
          </div>

          {/* Core Values */}
          <h3 className="text-3xl font-semibold text-gray-900 mb-12 text-center">Core Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value) => {
              const IconComponent = value.icon;
              return (
                <div key={value.number} className="bg-white p-8 border border-gray-200 hover:border-[#0099FF]/50 hover:shadow-xl transition-all duration-400">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-4xl font-bold text-[#0099FF]/20">{value.number}</div>
                    <IconComponent className="text-[#0099FF] mt-2" size={28} />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Services Portfolio */}
      <div className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Services Portfolio</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive offering — from traditional assurance &amp; tax to modern AI, transactions, risk, and technology consulting.
            </p>
          </div>

          {/* Modern Consulting Practices — 5 EY-style pillars */}
          <div className="mb-20">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="font-mono text-[11px] tracking-[0.25em] text-[#0099FF] uppercase mb-2">
                  Pillar Practices
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900">
                  Modern Consulting Practices
                </h3>
              </div>
              <a
                href="/services"
                className="text-sm font-medium text-[#0099FF] hover:text-gray-900 transition-colors inline-flex items-center gap-1"
              >
                Explore full Services page →
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
              {consultingPillars.map((p, idx) => (
                <div
                  key={p.id}
                  className="bg-[#0a0f1e] text-white p-7 hover:bg-[#101733] transition-colors duration-300 group"
                  data-testid={`profile-pillar-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-brand-yellow">
                      0{idx + 1}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40">
                      Pillar
                    </span>
                  </div>
                  <h4 className="font-heading text-xl font-bold text-white mb-1 leading-tight">
                    {p.title}
                  </h4>
                  <p className="text-xs text-brand-yellow mb-4 tracking-wide">{p.subtitle}</p>
                  <ul className="space-y-2">
                    {p.items.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[13px] text-white/75 leading-snug"
                      >
                        <span className="text-brand-yellow mt-1">▸</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Filler CTA tile to balance the 3-col grid */}
              <a
                href="/services"
                className="bg-brand-yellow text-gray-900 p-7 flex flex-col justify-between hover:bg-yellow-300 transition-colors duration-300 group"
                data-testid="profile-pillar-cta"
              >
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] mb-4">
                    EXPLORE
                  </div>
                  <h4 className="font-heading text-2xl font-black leading-tight mb-3">
                    See the full Services page
                  </h4>
                  <p className="text-sm text-gray-800 leading-snug">
                    Deeper dives, sector outcomes, and stats for each pillar.
                  </p>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 font-semibold">
                  Open /services
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </a>
            </div>
          </div>

          {/* Foundation Practice — existing CA services */}
          <div>
            <div className="mb-8">
              <div className="font-mono text-[11px] tracking-[0.25em] text-[#0099FF] uppercase mb-2">
                Foundation Practice
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-gray-900">
                Chartered Accountancy Services
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`bg-gray-50 border border-gray-200 overflow-hidden ${
                    service.id === 5 ? 'md:col-span-2' : ''
                  }`}
                >
                  <div className="bg-[#0099FF] p-6">
                    <h3 className="text-2xl font-semibold text-white">{service.title}</h3>
                  </div>
                  <div className="p-6">
                    <ul
                      className={`space-y-3 ${
                        service.id === 5 ? 'md:grid md:grid-cols-2 md:gap-x-8 md:space-y-0 md:gap-y-3' : ''
                      }`}
                    >
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <span className="text-[#0099FF] mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leadership Team */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Leadership Team</h2>
            <p className="text-xl text-gray-600">Experienced professionals driving excellence and innovation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((leader, idx) => (
              <div key={idx} className="bg-white border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-400">
                <div className="bg-gradient-to-br from-[#0099FF] to-[#0077CC] p-6 text-center">
                  <div className="w-28 h-28 rounded-full mx-auto mb-4 overflow-hidden bg-white/20 ring-2 ring-white/40 flex items-center justify-center">
                    {leader.photo ? (
                      <img
                        src={leader.photo}
                        alt={leader.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-top"
                        data-testid={`leader-photo-${idx}`}
                      />
                    ) : (
                      <Users className="text-white" size={40} />
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-1">{leader.name}</h3>
                  <p className="text-white/90 text-sm mb-2">{leader.role}</p>
                  <p className="text-white/70 text-xs">Qualified: {leader.qualified}</p>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {leader.expertise.map((skill, skillIdx) => (
                      <span key={skillIdx} className="bg-[#0099FF]/10 text-[#0099FF] px-3 py-1 text-xs font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industry Expertise */}
      <div className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Industry Expertise</h2>
            <p className="text-xl text-gray-600">Serving diverse sectors with specialized knowledge and insights</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 p-6 hover:border-[#0099FF]/50 hover:shadow-lg transition-all duration-400 group">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#0099FF] transition-colors">
                  {industry.name}
                </h3>
                <p className="text-sm text-gray-600">{industry.details}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold mb-4">Connect With Us</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We welcome the opportunity to discuss how R S T & Co. can support your business objectives. Our team is ready to provide tailored solutions that drive measurable results.
            </p>
          </div>

          {/* Office Locations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Head Office */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-7" data-testid="office-head">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[#0099FF]/20 border border-[#0099FF]/40">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-[#0099FF] uppercase">Head Office</span>
                </div>
                <MapPin className="text-[#0099FF]" size={24} />
              </div>
              <h4 className="font-semibold text-lg mb-3">Nagarabhavi, Bengaluru</h4>
              <a
                href="https://maps.app.goo.gl/AYAMY2bM1A49UYzx8"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-gray-300 hover:text-[#0099FF] transition-colors mb-4 leading-relaxed"
                data-testid="profile-address-link"
              >
                #222, 1st &amp; 2nd Floor, 80 Ft. Service Main Road, 12th Block BDA Layout, Marilingappa Extension, Nagarabhavi, Bengaluru, Karnataka – 560072
              </a>
              <div className="space-y-1.5 text-sm">
                <a href="tel:+918023188259" className="block text-gray-300 hover:text-[#0099FF] transition-colors">
                  <Phone className="inline mr-2 text-[#0099FF]" size={14} />
                  +91 80-23188259
                </a>
                <a href="tel:+919036393399" className="block text-gray-300 hover:text-[#0099FF] transition-colors">
                  <Phone className="inline mr-2 text-[#0099FF]" size={14} />
                  +91 9036393399
                </a>
              </div>
            </div>

            {/* Branch Office */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-7" data-testid="office-branch">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-brand-yellow/15 border border-brand-yellow/40">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-brand-yellow uppercase">Branch Office</span>
                </div>
                <MapPin className="text-brand-yellow" size={24} />
              </div>
              <h4 className="font-semibold text-lg mb-3">Vijayanagar, Bengaluru</h4>
              <a
                href="https://www.google.com/maps/search/?api=1&query=%2319%2C+1766%2C+5th+Main%2C+22nd+Cross+Road%2C+Govindarajanagar%2C+Vijayanagar%2C+Bengaluru+560079"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-gray-300 hover:text-brand-yellow transition-colors mb-4 leading-relaxed"
                data-testid="profile-branch-address-link"
              >
                #19, 1766, 5th Main, 22nd Cross Road, Govindarajanagar, Vijayanagar, Bengaluru – 560079
              </a>
              <div className="space-y-1.5 text-sm">
                <a href="tel:+918066065949" className="block text-gray-300 hover:text-brand-yellow transition-colors">
                  <Phone className="inline mr-2 text-brand-yellow" size={14} />
                  080-66065949
                </a>
              </div>
            </div>
          </div>

          {/* Email + Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 flex items-center gap-4">
              <Mail className="text-[#0099FF] flex-shrink-0" size={28} />
              <div>
                <h4 className="font-semibold mb-1 text-sm">Email</h4>
                <a href="mailto:info@rstandco.co.in" className="text-sm text-gray-300 hover:text-[#0099FF] transition-colors break-all">
                  info@rstandco.co.in
                </a>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 flex items-center gap-4">
              <Globe className="text-[#0099FF] flex-shrink-0" size={28} />
              <div>
                <h4 className="font-semibold mb-1 text-sm">Website</h4>
                <a href="https://www.rstandco.co.in" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-[#0099FF] transition-colors break-all">
                  www.rstandco.co.in
                </a>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-2xl italic text-gray-300">"Excellence in Professional Services Since 2011"</p>
          </div>

          {/* Embedded Google Map */}
          <div className="mt-16">
            <div className="font-mono text-[11px] tracking-[0.25em] text-[#0099FF] uppercase mb-4 text-center">
              Find Us
            </div>
            <div
              className="relative w-full overflow-hidden border border-white/20 shadow-2xl"
              style={{ paddingTop: '42%' }}
              data-testid="profile-map-embed"
            >
              <iframe
                title="R S T & Co. Office Location"
                src="https://www.google.com/maps?q=R+S+T+%26+Co.+Chartered+Accountants+%23222+80+Ft+Service+Main+Road+12th+Block+BDA+Layout+Nagarabhavi+Bengaluru+560072&output=embed"
                className="absolute inset-0 w-full h-full grayscale-[10%] contrast-110"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="text-center mt-4">
              <a
                href="https://maps.app.goo.gl/AYAMY2bM1A49UYzx8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#0099FF] hover:text-white text-sm font-medium transition-colors"
              >
                Open in Google Maps →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FirmProfileSection;